import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { CancelOrderModal } from '@pages/Trade/Portfolio/Modals/CancelOrderModal'
import { TPSLModal } from '@pages/Trade/Portfolio/Modals/TPSLModal'
// import { TargetPriceModal } from '@pages/Trade/Portfolio/Modals/TargetPriceModal'
import { TradeDetailsPendingModal } from '@pages/Trade/Portfolio/Modals/TradeDetailsPendingModal'
import { ValidTillModal } from '@pages/Trade/Portfolio/Modals/ValidTillModal'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import type { DxTradeExecutableSide, ProtectiveOrderType } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import type { DxtradeInstrumentMap } from '../hooks/useDxtradeInstruments'
import type {
  OrderCancelParams,
  UseFixTradingWebSocketResult,
} from '../hooks/useFixTradingWebSocket'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import {
  buildTradeWidgetUrl,
  formatDate,
  formatSymbol,
  getOpenLegTargetPrice,
  renderCellValue,
  renderSide,
  upperCaseFirstLetter,
} from '../utils/formatters'
import { getCancelOrderParams } from '../utils/orderHelpers'
import EditableCellContent from './EditableCellContent'
import { EmptyPortfolioState } from './EmptyPortfolioState'
import { LivePriceCell } from './LivePriceCell'
import RowActions from './RowActions'

import styles from './PendingOrders.module.scss'

type Props = {
  orders: PendingOrderWithLots[]
  isLoading: boolean
  cancelOrder: (params: OrderCancelParams) => string
  newOrder: UseFixTradingWebSocketResult['newOrder']
  changePendingOrderSlTp: UseFixTradingWebSocketResult['changePendingOrderSlTp']
  replaceOrder: UseFixTradingWebSocketResult['replaceOrder']
  accountCurrency?: CurrencyType
  accountCode?: string
  dxtradeInstruments?: DxtradeInstrumentMap
  hasActiveFilters?: boolean
}

type RowProps = {
  order: PendingOrderWithLots
  accountCurrency?: CurrencyType
  priceIncrement?: number | null
  replaceOrder: UseFixTradingWebSocketResult['replaceOrder']
  // onOpenTargetPrice: () => void
  onOpenTPSL: (type: ProtectiveOrderType) => void
  onOpenCancel: () => void
  onOpenInfo: () => void
  onOpenValidTill: () => void
  onRowClick: () => void
}

const PendingOrderRow: React.FC<RowProps> = memo(
  ({
    order,
    // onOpenTargetPrice,
    onOpenTPSL,
    onOpenCancel,
    onOpenInfo,
    onOpenValidTill,
    onRowClick,
    priceIncrement,
  }) => {
    const { t } = useTranslation()
    const { formatNumber } = useFormatNumber()
    const fractionDigits = getStepDecimals(priceIncrement)
    const typeLabel = upperCaseFirstLetter(order.type)
    const stopLoss: number | undefined = order.stopLoss ?? undefined
    const takeProfit: number | undefined = order.takeProfit ?? undefined
    const targetPrice = getOpenLegTargetPrice(order)
    return (
      <TableRow key={order.orderId} onClick={onRowClick}>
        <TableCell>{formatSymbol(order.instrument, order.description)}</TableCell>
        <TableCell sensitive>
          {renderSide(order.side, Body2)}{' '}
          {typeLabel && <Body2 className={styles.type}>{typeLabel}</Body2>}
        </TableCell>
        <TableCell align='right' sensitive>
          {renderCellValue(order.sizeLots)}
        </TableCell>
        <TableCell align='right'>
          <LivePriceCell
            trade={{
              symbol: order.instrument || '',
              side: order.side,
              priceIncrement,
            }}
            priceType='order'
          />
        </TableCell>
        <TableCell align='right' sensitive>
          {targetPrice != null ? (
            formatNumber(targetPrice, {
              minDecimalPlaces: fractionDigits,
              maxDecimalPlaces: fractionDigits,
            })
          ) : (
            <Body2>{'-'}</Body2>
          )}
          {/*<EditableCellContent*/}
          {/*  value={targetPrice}*/}
          {/*  renderValue={(v) =>*/}
          {/*    formatNumber(v as number, {*/}
          {/*      minDecimalPlaces: fractionDigits,*/}
          {/*      maxDecimalPlaces: fractionDigits,*/}
          {/*    })*/}
          {/*  }*/}
          {/*  editAriaLabel={'Portfolio.Edit Target Price'}*/}
          {/*  sensitive*/}
          {/*  onEdit={() => onOpenTargetPrice()}*/}
          {/*/>*/}
        </TableCell>
        <TableCell align='right'>
          <EditableCellContent
            value={stopLoss}
            renderValue={(v) =>
              formatNumber(v as number, {
                minDecimalPlaces: fractionDigits,
                maxDecimalPlaces: fractionDigits,
              })
            }
            editAriaLabel={'Portfolio.Edit Stop Loss'}
            sensitive
            onEdit={() => onOpenTPSL('stopLoss')}
          />
        </TableCell>
        <TableCell align='right'>
          <EditableCellContent
            value={takeProfit}
            renderValue={(v) =>
              formatNumber(v as number, {
                minDecimalPlaces: fractionDigits,
                maxDecimalPlaces: fractionDigits,
              })
            }
            editAriaLabel={'Portfolio.Edit Take Profit'}
            sensitive
            onEdit={() => onOpenTPSL('takeProfit')}
          />
        </TableCell>
        <TableCell align='right'>
          <EditableCellContent
            value={order.expireDate ?? undefined}
            editAriaLabel={'Portfolio.Edit Valid till'}
            renderValue={() => formatDate(order.expireDate ?? undefined)}
            iconType='calendar'
            onEdit={onOpenValidTill}
          />
        </TableCell>
        <TableCell align='right'>
          <RowActions
            infoAriaLabel={'Portfolio.Info'}
            closeAriaLabel={'Portfolio.Close'}
            closeTooltip={t('Portfolio.Cancel order tooltip')}
            onInfoClick={onOpenInfo}
            onCloseClick={onOpenCancel}
          />
        </TableCell>
      </TableRow>
    )
  }
)

export const PendingOrdersTable: React.FC<Props> = memo(function PendingOrdersTable({
  orders,
  isLoading,
  cancelOrder,
  newOrder,
  changePendingOrderSlTp,
  replaceOrder,
  accountCurrency,
  accountCode,
  dxtradeInstruments,
  hasActiveFilters,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // const targetPriceModal = useModal(TargetPriceModal)
  const tpslModal = useModal(TPSLModal)
  const cancelOrderModal = useModal(CancelOrderModal)
  const tradeDetailsPendingModal = useModal(TradeDetailsPendingModal)
  const validTillModal = useModal(ValidTillModal)

  const noResults = !orders?.length ? (
    <EmptyPortfolioState hasActiveFilters={hasActiveFilters} />
  ) : null

  return (
    <>
      <Table
        isLoading={isLoading}
        sticky='left'
        noResults={noResults}
        skeletonRows={5}
        skeletonColumns={9}
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t('Portfolio.Asset')}</TableHead>
            <TableHeadSort>{t('Portfolio.Type')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Size')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Current price')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Target price')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Stop Loss')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Take Profit')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Valid till')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Actions')}</TableHeadSort>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <PendingOrderRow
              key={order.orderId}
              order={order}
              priceIncrement={dxtradeInstruments?.[order.instrument || '']?.priceIncrement}
              replaceOrder={replaceOrder}
              // onOpenTargetPrice={() => {
              //   const instrument = dxtradeInstruments?.[order.instrument || '']
              //   if (!instrument || instrument.priceIncrement == null) {
              //     return
              //   }
              //   targetPriceModal.open({
              //     order,
              //     accountCurrency,
              //     accountCode,
              //     instrumentMeta: {
              //       pipSize: instrument.pipSize,
              //       priceIncrement: instrument.priceIncrement,
              //     },
              //     replaceOrder,
              //   })
              // }}
              onOpenTPSL={(type) => {
                const instrument = dxtradeInstruments?.[order.instrument || '']
                if (!instrument || instrument.priceIncrement == null) {
                  return
                }
                tpslModal.open({
                  order,
                  accountCurrency,
                  instrumentCurrency: (instrument.currency ?? undefined) as
                    | CurrencyType
                    | undefined,
                  accountCode,
                  instrumentMeta: {
                    pipSize: instrument.pipSize,
                    priceIncrement: instrument.priceIncrement,
                  },
                  initialActiveType: type,
                  cancelOrder,
                  newOrder,
                  changePendingOrderSlTp,
                  replaceOrder,
                })
              }}
              onOpenCancel={() =>
                cancelOrderModal.open({
                  order,
                  onConfirm: (onSettled) => {
                    const params = getCancelOrderParams(order)
                    if (!params) return false
                    return Boolean(cancelOrder({ ...params, onSettled }))
                  },
                })
              }
              onOpenInfo={() => {
                const instrument = dxtradeInstruments?.[order.instrument || '']
                if (!instrument || instrument.priceIncrement == null) {
                  return
                }
                tradeDetailsPendingModal.open({
                  order,
                  accountCurrency,
                  accountCode,
                  instrumentMeta: {
                    pipSize: instrument.pipSize,
                    priceIncrement: instrument.priceIncrement,
                  },
                  cancelOrder,
                  newOrder,
                  changePendingOrderSlTp,
                  replaceOrder,
                })
              }}
              onOpenValidTill={() =>
                validTillModal.open({
                  order,
                  accountCode,
                  replaceOrder,
                })
              }
              onRowClick={() =>
                navigate(
                  buildTradeWidgetUrl(
                    order.instrument || '',
                    (order.side ?? 'BUY') as DxTradeExecutableSide,
                    'order'
                  )
                )
              }
            />
          ))}
        </TableBody>
      </Table>
    </>
  )
})
